import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router-dom';
import styled from 'styled-components';

import EmptyError from '@/components/common/EmptyError';
import Loading from '@/components/Loading';
import { isSessionExpired } from '@/lib/sessionExpiry';
import {
  ChunkLoadRecovery,
  ChunkLoadRecoveryOptions,
  classifyChunkLoadFailure,
  getObservabilityContext,
  prepareChunkLoadRecovery,
  reportBrowserError,
  reportChunkLoadRecovery,
} from '@/observability';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80vh;

  div:nth-of-type(1) {
    font-size: 18px;
    font-weight: 600;
    margin: 24px auto 16px;
  }
`;

const RefreshButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  border: 0;
  border-radius: 6px;
  background: #4664f9;
  color: white;
  cursor: pointer;
  font: inherit;
  font-weight: 600;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`;

const HomeLink = styled.a`
  margin-top: 20px;
  padding: 9px 19px;
  border: 1px solid #4664f9;
  border-radius: 6px;
  color: #8bb9ff;
  font-weight: 600;
  text-decoration: none;
`;

const Reference = styled.small`
  margin-top: 16px;
  color: #8f9bb3;
`;

type ErrorViewProps = {
  error?: unknown;
  recoveryOptions?: ChunkLoadRecoveryOptions;
  reloadPage?: () => void;
  reportError?: boolean;
  source?: string;
};

type RecoveryFallback = ChunkLoadRecovery & { correlationId: string };

type ErrorScreenProps = {
  actionDisabled?: boolean;
  actionLabel?: string;
  details?: ReactNode;
  message: ReactNode;
  onAction?: () => void;
  reference?: string;
  showHome?: boolean;
  title: ReactNode;
};

function reloadCurrentPage() {
  window.location.reload();
}

export function ErrorScreen({
  actionDisabled,
  actionLabel,
  details,
  message,
  onAction,
  reference,
  showHome = false,
  title,
}: ErrorScreenProps) {
  // error screens render outside <Suspense>, so they must not wait for a translation file
  const { t } = useTranslation(undefined, { useSuspense: false });
  return (
    <Container>
      <EmptyError />
      <div>{title}</div>
      <div>{message}</div>
      {(onAction || showHome) && (
        <Actions>
          {onAction && (
            <RefreshButton type='button' disabled={actionDisabled} onClick={onAction}>
              {actionLabel}
            </RefreshButton>
          )}
          {showHome && <HomeLink href='/'>{t('Back to home')}</HomeLink>}
        </Actions>
      )}
      {reference && <Reference>{t('Reference: {{reference}}', { reference })}</Reference>}
      {details}
    </Container>
  );
}

/**
 * Error component for route error
 */
export function ErrorView({
  error,
  recoveryOptions,
  reloadPage = reloadCurrentPage,
  reportError = true,
  source = 'react',
}: ErrorViewProps) {
  // error screens render outside <Suspense>, so they must not wait for a translation file
  const { t } = useTranslation(undefined, { useSuspense: false });
  const handledRecoveryError = useRef<unknown>();
  const [recoveryFallback, setRecoveryFallback] = useState<RecoveryFallback>();
  const chunkFailure = classifyChunkLoadFailure(error);

  useEffect(() => {
    if (reportError && error !== undefined) reportBrowserError(source, error);
  }, [error, reportError, source]);

  useEffect(() => {
    if (!chunkFailure || handledRecoveryError.current === error) return;
    handledRecoveryError.current = error;
    const recovery = prepareChunkLoadRecovery(error, recoveryOptions);
    if (!recovery || recovery.action === 'wait-for-reload') return;

    reportChunkLoadRecovery(recovery, source);
    if (recovery.action === 'reload') reloadPage();
    else setRecoveryFallback({ ...recovery, correlationId: getObservabilityContext().correlationId });
  }, [chunkFailure, error, recoveryOptions, reloadPage, source]);

  const errorDetails = typeof error === 'object' ? JSON.stringify(error, null, 2) : String(error);
  const hasErrorDetails = errorDetails && errorDetails !== '{}';

  if (isSessionExpired()) return <Loading />;
  if (chunkFailure && !recoveryFallback) return <Loading title={t('Updating Alphie Trader…')} />;

  if (recoveryFallback) {
    return (
      <ErrorScreen
        actionLabel={t('Refresh now')}
        message={t("We couldn't load part of this version. Refresh this tab to continue.")}
        onAction={reloadPage}
        reference={recoveryFallback.correlationId}
        title={t('Alphie Trader needs to refresh')}
      />
    );
  }

  return (
    <ErrorScreen
      actionLabel={t('Refresh page')}
      details={
        process.env.NODE_ENV === 'development' && hasErrorDetails ? <pre style={{ color: 'red' }}>{errorDetails}</pre> : undefined
      }
      message={t('Something went wrong. Refresh this page or return home.')}
      onAction={reloadPage}
      reference={getObservabilityContext().correlationId}
      showHome
      title={t('Oops! Lost in space')}
    />
  );
}

export default function ErrorPage() {
  return <ErrorView error={useRouteError()} source='route' />;
}
